export interface CatalogueNode {
  id: number | string
  nameEn: string
  nameAr: string
  isChecked?: boolean
  duration?: number | null
  price?: number | null
  children?: CatalogueNode[]
}

export type CatalogueCategory = CatalogueNode

export interface CatalogueServicePayload {
  b8ShareCategoryInfoId: string
  serviceId?: number
  subServiceId?: number | null
  subSubServiceId?: number | null
}

export type CataloguePayload = CatalogueServicePayload[]

export type CheckState = 'checked' | 'unchecked' | 'indeterminate'

export const ALL_CATEGORIES_ID = 'all'

// --------------------------------------------------
// Normalize API response
// --------------------------------------------------

export const normalizeCatalogue = (
  data: CatalogueNode[] | CatalogueNode | null | undefined
): CatalogueCategory[] => {
  if (!data) return []

  return Array.isArray(data) ? data : [data]
}

// --------------------------------------------------
// Filter Category on FE
// --------------------------------------------------

export const filterCategories = (
  categories: CatalogueCategory[],
  selectedCategoryKey: string
): CatalogueCategory[] => {
  if (selectedCategoryKey === ALL_CATEGORIES_ID) {
    return categories
  }

  const index = Number(selectedCategoryKey)

  if (
    !Number.isInteger(index) ||
    index < 0 ||
    index >= categories.length
  ) {
    return []
  }

  return [categories[index]]
}

// --------------------------------------------------
// Unique node key by position
// --------------------------------------------------

/**
 * Use positional paths instead of IDs because
 * multiple nodes may have the same ID.
 *
 * Examples:
 * category-0:
 * category-0:0
 * category-0:0/1
 */
export const nodeKey = (categoryKey: string, path: number[]): string =>
  `${categoryKey}:${path.join('/')}`

export const getLeafKeys = (node: CatalogueNode, categoryKey: string, path: number[]): string[] => {
  const children = node.children || []

  if (children.length === 0) {
    return [nodeKey(categoryKey, path)]
  }

  return children.flatMap((child, index) =>
    getLeafKeys(
      child,
      categoryKey,
      [...path, index]
    )
  )
}

// --------------------------------------------------
// Checkbox state
// --------------------------------------------------

export const getCheckState = (
  node: CatalogueNode,
  categoryKey: string,
  path: number[],
  selectedKeys: Set<string>
): CheckState => {
  const leaves = getLeafKeys(
    node,
    categoryKey,
    path
  )

  if (!leaves.length) return 'unchecked'

  let selectedCount = 0

  for (const key of leaves) {
    if (selectedKeys.has(key)) {
      selectedCount += 1
    }
  }

  if (selectedCount === 0) {
    return 'unchecked'
  }

  if (selectedCount === leaves.length) {
    return 'checked'
  }

  return 'indeterminate'
}

// --------------------------------------------------
// Collect leaf IDs
// --------------------------------------------------

export const collectLeafIds = (
  node: CatalogueNode
): string[] => {
  const children = node.children || []

  if (!children.length) {
    return [String(node.id)]
  }

  return children.flatMap(collectLeafIds)
}

// --------------------------------------------------
// Initial selection from API
// --------------------------------------------------

/**
 * Initialize selectedKeys using isChecked.
 *
 * Rules:
 * 1. Leaf isChecked=true -> selected
 * 2. Empty Category isChecked=true -> selected
 * 3. Parent state is calculated from its children
 * 4. Parent isChecked=true does not automatically
 *    select all descendants, because BE may mark
 *    a parent checked when only some children exist
 *    in the user's service tree.
 */
export const getInitialSelectedKeys = (
  categories: CatalogueCategory[]
): Set<string> => {
  const selectedKeys = new Set<string>()

  const traverse = (
    node: CatalogueNode,
    categoryKey: string,
    path: number[]
  ): void => {
    const children = node.children || []

    if (children.length === 0) {
      if (node.isChecked === true) {
        selectedKeys.add(
          nodeKey(categoryKey, path)
        )
      }

      return
    }

    children.forEach((child, index) => {
      traverse(
        child,
        categoryKey,
        [...path, index]
      )
    })
  }

  categories.forEach((category, index) => {
    traverse(
      category,
      `category-${index}`,
      []
    )
  })

  return selectedKeys
}

// --------------------------------------------------
// Build API payload
// --------------------------------------------------

/**
 * API payload format:
 *
 * [
 *   {
 *     b8ShareCategoryInfoId: "category-guid",
 *     serviceId: 123,
 *     subServiceId: 456,
 *     subSubServiceId: 789
 *   }
 * ]
 *
 * Hierarchy:
 * Category -> Service -> SubService -> SubSubService
 *
 * Only selected leaf paths are submitted.
 *
 * IMPORTANT:
 * Empty Categories can be checked in the UI,
 * but cannot be represented by this payload
 * because the BE contract requires serviceId.
 */

export const buildCataloguePayload = (
  categories: CatalogueCategory[],
  selectedKeys: Set<string>
): CataloguePayload => {
  const payload: CataloguePayload = []

  categories.forEach((category, categoryIndex) => {
    const b8ShareCategoryInfoId = String(category.id ?? '').trim()
    if (!b8ShareCategoryInfoId) return

    const categoryKey = `category-${categoryIndex}`

    const collect = (node: CatalogueNode, path: number[], ids: number[]) => {
      const children = node.children || []

      if (!children.length) {
        if (!selectedKeys.has(nodeKey(categoryKey, path))) {
          return
        }

        const [serviceId, subServiceId, subSubServiceId] = ids

        const item: CatalogueServicePayload = {
          b8ShareCategoryInfoId,
        }

        if (serviceId !== undefined && Number.isFinite(serviceId)) {
          item.serviceId = serviceId
        }

        if (subServiceId !== undefined && Number.isFinite(subServiceId)) {
          item.subServiceId = subServiceId
        }

        if (subSubServiceId !== undefined && Number.isFinite(subSubServiceId)) {
          item.subSubServiceId = subSubServiceId
        }

        payload.push(item)
        return
      }

      children.forEach((child, index) => {
        collect(
          child,
          [...path, index],
          [...ids, Number(child.id)]
        )
      })
    }

    collect(category, [], [])
  })

  return payload
}

